import { lazy, Suspense, useCallback } from 'react';

import { DOBOX_FORM_MODE } from '../../pages/private/DoBox/components/DoBoxFormModal/types/types';
import { useDoBoxAiInitialValues } from '../../pages/private/DoBox/hooks/useDoBoxAiInitialValues';
import { ASSISTANT_MODE } from '../../pages/private/Flows/components/AiAssistantDialog.constants';
import { useCreateDoBoxFormStore } from '../../store/CreateDoBoxForm/useCreateDoBoxFormStore';
import { useInboxEntriesStore } from '../../store/Inbox/InboxEntries/useInboxEntriesStore';
import { traceDoBoxForm } from '../../utils/debug/doBoxFormTrace';

const DoBoxFormModal = lazy(() =>
  import(
    '../../pages/private/DoBox/components/DoBoxFormModal/DoBoxFormModal'
  ).then((m) => ({ default: m.DoBoxFormModal })),
);

const AiAssistantDialog = lazy(() =>
  import('../../pages/private/Flows/components/AiAssistantDialog').then(
    (m) => ({ default: m.AiAssistantDialog }),
  ),
);

export function GlobalDoBoxFormModal() {
  const isOpen = useCreateDoBoxFormStore((s) => s.isOpen);
  const isAiDialogOpen = useCreateDoBoxFormStore((s) => s.isAiAssistantOpen);
  const taskSubgroupId = useCreateDoBoxFormStore((s) => s.taskSubgroupId);
  const close = useCreateDoBoxFormStore((s) => s.close);
  const closeAiAssistant = useCreateDoBoxFormStore((s) => s.closeAiAssistant);
  const activeTaskGroupId = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.taskGroupId,
  );

  const {
    aiInitialValues,
    aiNoticeText,
    aiFormKey,
    clearAiInitialValues,
    handleAiDoBoxResult,
    handleAiEntryCreated,
    handleAiFormClose,
  } = useDoBoxAiInitialValues();

  const handleCloseDoBoxForm = useCallback(() => {
    traceDoBoxForm('global:closeForm', {}, { withStack: true });
    handleAiFormClose(close);
  }, [handleAiFormClose, close]);

  const handleOpenAiAssistant = useCallback(() => {
    traceDoBoxForm('global:openAiAssistant');
    const { taskSubgroupId: current } = useCreateDoBoxFormStore.getState();
    clearAiInitialValues();
    useCreateDoBoxFormStore
      .getState()
      .openAiAssistant({ taskSubgroupId: current ?? undefined });
  }, [clearAiInitialValues]);

  const handleCreateManually = useCallback(() => {
    traceDoBoxForm('global:createManually');
    clearAiInitialValues();
    const { taskSubgroupId: current, open } =
      useCreateDoBoxFormStore.getState();
    open({ taskSubgroupId: current ?? undefined });
  }, [clearAiInitialValues]);

  const handleAiAssistantResult = useCallback(
    (result: unknown) => {
      traceDoBoxForm('global:aiResult');
      const isHandled = handleAiDoBoxResult(result);

      if (!isHandled) {
        return;
      }

      const { taskSubgroupId: current, open } =
        useCreateDoBoxFormStore.getState();
      open({ taskSubgroupId: current ?? undefined });
    },
    [handleAiDoBoxResult],
  );

  const canRestoreTaskSubgroupId =
    !aiInitialValues?.taskGroupId ||
    aiInitialValues.taskGroupId === activeTaskGroupId;

  if (!isOpen && !isAiDialogOpen) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <Suspense fallback={null}>
          <DoBoxFormModal
            key={aiFormKey}
            isOpen={isOpen}
            onClose={handleCloseDoBoxForm}
            mode={DOBOX_FORM_MODE.CREATE}
            initialValues={{
              ...aiInitialValues,
              taskSubgroupId: canRestoreTaskSubgroupId
                ? (taskSubgroupId ?? undefined)
                : undefined,
            }}
            onSuccess={handleAiEntryCreated}
            aiNotice={aiNoticeText ?? undefined}
            onOpenAiAssistant={handleOpenAiAssistant}
          />
        </Suspense>
      )}
      {isAiDialogOpen && (
        <Suspense fallback={null}>
          <AiAssistantDialog
            isOpen={isAiDialogOpen}
            onClose={closeAiAssistant}
            mode={ASSISTANT_MODE.DOBOX}
            onResult={handleAiAssistantResult}
            activeTaskGroupId={activeTaskGroupId}
            onCreateManually={handleCreateManually}
          />
        </Suspense>
      )}
    </>
  );
}
